import React, { useMemo, useState } from 'react';
import { Popover } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ConditionalRender from '../../shared/components/ConditionalRender';
import { AppRoutes } from '../../routes/routeConstants/appRoutes';
import { AllowedRoles } from '../../routes/routeConstants/allowedRoles';

interface UserMenuItem {
  label: string;
  icon?: string;
  route: string;
}

const MANAGEMENT_ROUTES = new Set([AppRoutes.MY_PROFILE, AppRoutes.CLIENT_MANAGER, AppRoutes.USER_MANAGER, AppRoutes.GROUP_MANAGER]);

const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMatchingRoute = MANAGEMENT_ROUTES.has(pathname);
  const { authenticated, canAccess, needsClientSelection } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const isCesAdminWithSelectedClient = canAccess({ allowedRoles: AllowedRoles.ONLY_CES_ADMIN }) && !needsClientSelection;

  const userMenuItems: UserMenuItem[] = useMemo(
    () => [
      { label: 'My Profile', icon: '/assets/user-icon.svg', route: AppRoutes.MY_PROFILE },
      ...(canAccess({ allowedRoles: AllowedRoles.ONLY_CES_ADMIN }) ? [{ label: 'CLIENT MANAGER', route: AppRoutes.CLIENT_MANAGER }] : []),
      ...(canAccess({ allowedRoles: AllowedRoles.ONLY_ADMINS }) && !needsClientSelection
        ? [
            { label: 'USER MANAGER', route: AppRoutes.USER_MANAGER },
            { label: 'GROUP MANAGER', route: AppRoutes.GROUP_MANAGER },
          ]
        : []),
    ],
    [canAccess, needsClientSelection],
  );

  const handleUserMenuItemClick = (route: string) => {
    setUserMenuOpen(false);
    navigate(route);
  };

  const userMenuContent = (
    <div className="app-shell__user-menu" role="menu" aria-label="User menu">
      {userMenuItems.map((item) => (
        <button
          key={item.label}
          type="button"
          className={`app-shell__user-menu-item ${pathname === item.route ? 'app-shell__user-menu-item--active' : ''}`}
          role="menuitem"
          onClick={() => handleUserMenuItemClick(item.route)}
        >
          {item.icon && <img src={item.icon} alt="" aria-hidden="true" />}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <header className="app-shell__topbar">
      <div className="app-shell__brand">
        <ConditionalRender visible={authenticated}>
          <img className={'app-shell__menu'} src={'/assets/menu_toggle.svg'} alt="Menu" />
        </ConditionalRender>
        <img className={'app-shell__logo'} src={'/assets/MapAki-Logo-Light-2024.svg'} alt="MapAki" />
      </div>

      <ConditionalRender visible={authenticated}>
        <div className="app-shell__actions">
          <img src={'/assets/notifications-icon.svg'} alt="Notifications" />
          <img src={'/assets/customization-icon.svg'} alt="Customization" />
          <Popover
            content={userMenuContent}
            trigger="click"
            placement="bottomRight"
            overlayClassName="app-shell__user-popover"
            arrow={false}
            open={userMenuOpen}
            onOpenChange={setUserMenuOpen}
          >
            <button type="button" className="app-shell__user-trigger" aria-label="Open user menu" aria-haspopup="menu" aria-expanded={userMenuOpen}>
              <img
                src={
                  isCesAdminWithSelectedClient
                    ? '/assets/clientManagerIcon.svg'
                    : isMatchingRoute
                      ? '/assets/user-icon-active.svg'
                      : '/assets/user-icon.svg'
                }
                alt=""
                aria-hidden="true"
              />
            </button>
          </Popover>
        </div>
      </ConditionalRender>
    </header>
  );
};

export default AppHeader;
